/* global FACEBOOK_PIXEL_ID, ENVIRONMENT, BASE_URL */

const ReviewController = {};

ReviewController.install = (Vue) => {
	/**
	 * Are we running on production?
	 * @returns {boolean}
	 */
	Vue.isProduction = () => {
		return ENVIRONMENT === 'production';
	};

	/**
	 * Are we running on development?
	 * @returns {boolean}
	 */
	Vue.isDevelopment = () => !Vue.isProduction();

	/**
	 * Gets element to mount development component to
	 *
	 * @returns {object}
	 */
	Vue.appContainer = () => {
		let devElement = document.getElementById('app-reviews');
		if (!devElement) {
			devElement = document.querySelector('body[data-reviews]');
		}
		return devElement;
	};

	/**
	 * Initialize Snowplow Script
	 */
	Vue.InitSnowplow = () => {
		if (Vue.isDevelopment()) {
			return Promise.resolve();
		}

		if (typeof window.snowplow !== 'undefined') {
			return Promise.resolve();
		}

		const loadTracker = new Promise((resolve, reject) => {
			const head = document.head || document.getElementsByTagName('head')[0];
			const script = document.createElement('script');

			script.text = `;(function(p,l,o,w,i,n,g){if(!p[i]){p.GlobalSnowplowNamespace=p.GlobalSnowplowNamespace||[];
			p.GlobalSnowplowNamespace.push(i);p[i]=function(){(p[i].q=p[i].q||[]).push(arguments) };p[i].q=p[i].q||[];
			n=l.createElement(o);g=l.getElementsByTagName(o)[0];n.async=1; n.src=w;g.parentNode.insertBefore(n,g)}}
			(window,document,"script","//snow.prtl.co/3-3-1.js","snowplow"));`;

			head.appendChild(script);

			script.onload = resolve;
			script.onerror = reject;
		});

		const initTracker = new Promise((resolve, reject) => {
			if (typeof window.snowplow === 'undefined') {
				return reject(new Error('ReviewController.InitSnowplow: window.snowplow is not defined.'));
			}

			window.snowplow('newTracker', '1.0.0', 'collect.studyportals.com', {
				appId: 'reviews',
				platform: 'web',
				cookieDomain: null,
				discoverRootDomain: true,
				encodeBase64: false,
				respectDoNotTrack: false,
				userFingerprint: true,
				userFingerprintSeed: 6385926734,
				sessionCookieTimeout: 3600,
				eventMethod: 'post',
				maxPostBytes: 5000,
				crossDomainLinker: function (linkElement) {
					const destinationDomain = new URL(linkElement.href).hostname;

					if (window.location.hostname === destinationDomain) {
						return false;
					}

					const regex = new RegExp('\\w{3,}\\.\\w+portal\\.(com|fyi)$');

					return regex.test(destinationDomain) || [
						'sl.prtl.co',
						'sl.tst.prtl.co'
					].includes(destinationDomain);
				},
				contexts: {
					webPage: true,
					performanceTiming: true,
					gaCookies: true,
					geolocation: false
				}
			});

			window.snowplow('enableLinkClickTracking', { pseudoClicks: true });
			window.snowplow('enableErrorTracking');
			window.snowplow('enableActivityTracking', { minimumVisitLength: 20, heartbeatDelay: 20 });
			window.snowplow('trackPageView');

			setInterval(function () {
				window.snowplow('refreshLinkClickTracking');
			}, 2500);

			return resolve();
		});

		// Initialize a tracker when the microservice is loaded outside the Portal
		return loadTracker.then(initTracker);
	};

	/**
	 * Sends an event to RedShift, using SnowPlowTracker
	 *
	 * @param {object} router (Vue router to determine which widget triggered the event)
	 * @param {string} action
	 * @param {string} label
	 * @param {string} property (optional)
	 * @param {number} value (optional)
	 */
	Vue.SnowplowEvent = (router, action, label, property, value) => {
		if (!router || !action) {
			console.warn('ReviewController.SnowplowEvent: required parameters not found.');
			return;
		}

		label = Vue.processTrackingLabel(router, label);

		try {
			window.snowplow('trackStructEvent', {
				category: 'Reviews',
				action: action,
				label: label,
				property: property,
				value: value
			});
		} catch (error) {
			console.warn('ReviewController.SnowplowEvent: we could not store data in Redshift.', {
				data: {
					action,
					label,
					property,
					value
				},
				error
			});
		}
	};

	/**
	 * Processes/validates all label properties & return them in piped format
	 *
	 * @param {Object} router (Vue router to determine which widget triggered the event)
	 * @param {Object} labels
	 *
	 *  @returns {string}
	 */
	Vue.processTrackingLabel = (router, labels) => {
		const validatedLabels = [];
		const allowedLabels = [
			'university_id',
			'review_id',
			'triggered_by',
			'rating_quantity',
			'rating_average',
			'review_quantity',
			'field_value',
			'field_id',
			'characters',
			'valid',
			'blank_fields',
			'filled_fields',
			'asset_channel',
			'asset_media'
		];
		try {
			labels['triggered_by'] = router.currentRoute.name;
		} catch (err) {
			console.warn(`ReviewController.processTrackingLabel: Trigger by could not be defined from routing`);
		}

		// if provided properties are valid labels to track
		for (let label in labels) {
			if (labels.hasOwnProperty(label)) {
				if (allowedLabels.indexOf(label) !== -1) {
					validatedLabels.push(label + ':' + labels[label]);
				} else {
					console.warn(`ReviewController.processTrackingLabel: Label property not allowed: ${label}`);
				}
			}
		}

		return validatedLabels.join('|');
	};

	/**
	 * Initialize Facebook Pixel Script
	 */
	Vue.InitFacebookPixel = () => {
		if (FACEBOOK_PIXEL_ID === '') {
			console.warn('FACEBOOK_PIXEL_ID is not set, Facebook Pixel will not be loaded.');
			return Promise.resolve();
		}

		if (Vue.isDevelopment()) {
			return Promise.resolve();
		}

		if (typeof window.fbq !== 'undefined') {
			return Promise.resolve();
		}

		return new Promise((resolve, reject) => {
			const head = document.head || document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			const facebookPixelScript = `//connect.facebook.net/en_US/fbevents.js`;

			script.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
			n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
			n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
			t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
			document,'script','${facebookPixelScript}');
			
			fbq('init', '${FACEBOOK_PIXEL_ID}');
			`;

			head.appendChild(script);

			script.onload = resolve;
			script.onerror = reject;
		});
	};

	/**
	 * Load external stylesheet
	 * @param {string} url
	 *
	 * returns {Promise}
	 */
	Vue.LoadStylesheet = (url) => {
		return new Promise((resolve, reject) => {
			const linkTags = Array.prototype.slice.call(
				document.getElementsByTagName('link')
			).filter(link => {
				return link.href.indexOf(url) !== -1;
			});

			if (linkTags.length === 0) {
				const head = document.head || document.getElementsByTagName('head')[0];
				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = url;

				head.appendChild(link);

				link.onload = resolve;
				link.onerror = reject;
			}
		});
	};

	/**
	 * Returns the static image url
	 * @param {string} image
	 *
	 * returns {string}
	 */
	Vue.getStaticImage = (image) => {
		return BASE_URL + '/dist/static/' + image;
	};
};

export default ReviewController;
